// The floor plan — where things are on a venue's floor (F-ORG-03; spec/13 §3.4, spec/04 §3).
//
//   GET /v1/organizations/{o}/venues/{v}/floor-plan?floor=0   the plan, with an ETag        floor:read
//   PUT /v1/organizations/{o}/venues/{v}/floor-plan?floor=0   save it, If-Match required    floor:arrange
//
// A grid (up to 60 × 100 cells) with the zones' rectangles and the fixtures on it — the counter, the
// entrance, the walls. Saved whole, as arrange mode saves it: the console edits a copy and sends the
// lot, so the plan on the server is always one the console validated and the server re-validated
// with the same rules (`floorPlanProblems` in `@clavian/domain`), and refused with the same messages.
//
// **Concurrency.** Two managers arranging at once must not silently overwrite each other, so the
// plan carries an `ETag` and a save must send the one it read (spec/08 §2.5). A floor nobody has
// arranged yet reads as an empty plan at version 0, and the first save sends `If-Match: "0"`.
//
// **Stations** are placed on the plan too (spec/04 §3's `floor_item` of kind `station`, F-STN-19, 2026-10-01): each
// placed station is a rectangle (a 1 × 1 tile unless resized), two stations never share a square, and a station is on
// one floor at most — the stations placed on other floors come back in `stationsElsewhere`, so the editor's tray of
// unplaced stations leaves them out, and placing one here is refused until it is taken off the other floor. A counter
// PC (Clavian Counter) is never on the floor.
//
// **Several floors** (levels 0–9, P1 in spec/13 §3.4): each is its own plan with its own ETag; `floorsInUse` lists the
// levels that have a saved plan or a zone, so the editor offers them. A save pushes `floor.plan.updated` on
// `venue:{id}:floor` and every console re-reads that floor.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { StationIdSchema, VenueIdSchema, ZoneIdSchema } from "../ids/schema.js";
import { FixtureKindSchema, FLOOR_MAX, FLOOR_MAX_COLUMNS, FLOOR_MAX_ROWS, FLOOR_MIN, FloorNumberSchema, gridRectShape, MAX_FIXTURES_PER_FLOOR, MAX_ZONES_PER_FLOOR, } from "../primitives/floor.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { FLOOR_ARRANGE, FLOOR_READ } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
/** The size of a floor nobody has arranged yet: room to start, well inside the limits. */
export const DEFAULT_FLOOR_COLUMNS = 24;
export const DEFAULT_FLOOR_ROWS = 16;
export const FloorZoneSchema = z
    .object({ zoneId: ZoneIdSchema, ...gridRectShape })
    .openapi('FloorZone', { description: 'A zone’s rectangle on the plan. Each zone appears once, on its own floor.' });
export const FloorFixtureSchema = z
    .object({
    kind: FixtureKindSchema,
    label: z.string().trim().max(40).nullable(),
    ...gridRectShape,
})
    .openapi('FloorFixture', { description: 'Something on the floor that is not a station: a counter, a wall, the stairs.' });
export const FloorStationSchema = z
    .object({ stationId: StationIdSchema, ...gridRectShape })
    .openapi('FloorStation', { description: 'Where a station’s tile sits on the plan. A station is on one floor at most.' });
export const FloorStationElsewhereSchema = z
    .object({ stationId: StationIdSchema, floor: FloorNumberSchema })
    .openapi('FloorStationElsewhere', { description: 'A station already placed on another floor of the venue.' });
export const FloorPlanSchema = z
    .object({
    venueId: VenueIdSchema,
    floor: FloorNumberSchema,
    columns: z.int().min(1).max(FLOOR_MAX_COLUMNS),
    rows: z.int().min(1).max(FLOOR_MAX_ROWS),
    zones: z.array(FloorZoneSchema).max(MAX_ZONES_PER_FLOOR),
    fixtures: z.array(FloorFixtureSchema).max(MAX_FIXTURES_PER_FLOOR),
    /** The stations placed on this floor. */
    stations: z.array(FloorStationSchema).max(FLOOR_MAX_COLUMNS * FLOOR_MAX_ROWS),
    /** Stations placed on the venue's other floors — not in this floor's tray. */
    stationsElsewhere: z.array(FloorStationElsewhereSchema).max(FLOOR_MAX_COLUMNS * FLOOR_MAX_ROWS),
    /** The levels with a saved plan or a zone, ascending; always includes this one. */
    floorsInUse: z.array(FloorNumberSchema).max(FLOOR_MAX - FLOOR_MIN + 1),
    /** Also the ETag. 0 for a floor nobody has arranged. */
    version: z.int().min(0),
    /** Null until the first save. */
    updatedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('FloorPlan', { description: 'One floor of a venue: its grid, its zones’ rectangles and its fixtures.' });
export const PutFloorPlanSchema = z
    .object({
    columns: z.int().min(1).max(FLOOR_MAX_COLUMNS),
    rows: z.int().min(1).max(FLOOR_MAX_ROWS),
    // The array bounds are deliberately looser than the rules: a 31st zone is refused by
    // `floorPlanProblems` with the console's own message, not by a schema's generic one.
    zones: z.array(FloorZoneSchema).max(FLOOR_MAX_COLUMNS * FLOOR_MAX_ROWS),
    fixtures: z.array(FloorFixtureSchema).max(FLOOR_MAX_COLUMNS * FLOOR_MAX_ROWS),
    /** The stations placed on this floor; the plan is saved whole, so a station left out (or no list) is taken off it. */
    stations: z.array(FloorStationSchema).max(FLOOR_MAX_COLUMNS * FLOOR_MAX_ROWS).default([]),
})
    .strict()
    .openapi('PutFloorPlan');
export const FloorPlanQuerySchema = z
    .object({
    floor: z.coerce
        .number()
        .int()
        .min(FLOOR_MIN)
        .max(FLOOR_MAX)
        .default(FLOOR_MIN)
        .openapi({ description: 'The level: 0, the ground floor, unless the venue has more.' }),
})
    .strict()
    .openapi('FloorPlanQuery');
const floorPlanResponse = (description) => ({
    description,
    headers: z.object({ ETag: z.string().openapi({ description: 'The version to send back as If-Match.' }) }),
    content: { [JSON_CONTENT_TYPE]: { schema: FloorPlanSchema } },
});
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const venueNotFound = problemResponse('No such venue in this organisation, or not one you can see.', 'not-found');
export const getFloorPlanRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/floor-plan',
    operationId: 'getFloorPlan',
    tags: ['floor'],
    summary: 'Read one floor’s plan',
    description: 'Returns the plan and its `ETag`; an unarranged floor is an empty plan at version 0. Requires `floor:read`.',
    [PERMISSION_EXTENSION]: FLOOR_READ,
    request: { params: VenueScopeParamsSchema, query: FloorPlanQuerySchema },
    responses: {
        200: floorPlanResponse('The plan.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const putFloorPlanRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/floor-plan',
    operationId: 'putFloorPlan',
    tags: ['floor'],
    summary: 'Save one floor’s plan',
    description: 'Replaces the plan. Send the `ETag` you read as `If-Match`; a stale one is 412 and nothing is saved. ' +
        `Zones may not overlap, a floor holds at most ${MAX_ZONES_PER_FLOOR} zones and ${MAX_FIXTURES_PER_FLOOR} fixtures, ` +
        'everything stays inside the grid, and each zone appears once, on its own floor — the same rules, and the same ' +
        'messages, as arrange mode. Stations never share a square, and a station is on one floor at most. Pushes ' +
        '`floor.plan.updated` on `venue:{id}:floor`. Requires `floor:arrange`. Audited.',
    [PERMISSION_EXTENSION]: FLOOR_ARRANGE,
    request: {
        params: VenueScopeParamsSchema,
        query: FloorPlanQuerySchema,
        headers: z.object({
            'if-match': z.string().min(1).max(64).openapi({ description: 'The ETag this save is based on.' }),
        }),
        body: { content: { [JSON_CONTENT_TYPE]: { schema: PutFloorPlanSchema } }, required: true },
    },
    responses: {
        200: floorPlanResponse('The plan as saved.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        409: problemResponse('A station on this plan was placed on another floor while it was being saved.', 'conflict'),
        412: problemResponse('Someone else saved this floor since you read it.', 'stale-write'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=floor-plan.js.map